<script setup>
import { BLink, BPopover, vBTooltip } from 'bootstrap-vue-next';
import { computed, ref } from 'vue';

import { numberProps } from '~/common/FieldFormats';

import ContributorLevelBadge from './ContributorLevelBadge.vue';
import HackathonWinnerBadge from './HackathonWinnerBadge.vue';
import PointsDocumentationTooltip from './PointsDocumentationTooltip.vue';
import TableWithBusyState from './TableWithBusyState.vue';
import UserWithGitLabProfileLink from './UserWithGitLabProfileLink.vue';

import PajamasCopyToClipboard from '~icons/pajamas/copy-to-clipboard';
import PajamasReviewCheckmark from '~icons/pajamas/review-checkmark';

const props = defineProps({
  contributors: {
    type: Array,
    default: () => [],
  },
  totals: {
    type: Object,
    default: null,
  },
  busy: {
    type: Boolean,
    default: false,
  },
  hackathon: {
    type: Boolean,
    default: false,
  },
  filterData: {
    type: Object,
    default: () => {
      return {};
    },
  },
  stickyHeader: {
    type: Boolean,
    default: false,
  },
});

const justCopiedTable = ref(false);
const justCopiedUsernames = ref(false);

const allFields = [
  {
    key: 'username',
    label: 'Username',
  },
  {
    key: 'contributor_level',
    label: 'Level',
    class: 'text-center',
    tdClass: 'p-0',
  },
  {
    key: 'prize',
    label: 'Prize',
    description: 'Hackathon winner',
    class: 'text-center',
    tdClass: 'p-0',
  },
  {
    key: 'code_points',
    label: 'Code',
    description: 'Merge request points',
    ...numberProps,
    popover: [
      { label: 'Opened', field: 'opened_merge_requests' },
      { label: 'Merged', field: 'merged_merge_requests' },
      { label: 'Linked', field: 'merged_with_issues' },
      { label: 'Commits', field: 'merged_commits' },
    ],
  },
  {
    key: 'plan_points',
    label: 'Plan',
    description: 'Issue, note, label, and closing points',
    ...numberProps,
    popover: [
      { label: 'Opened issues', field: 'opened_issues' },
      { label: 'Added notes', field: 'added_notes' },
      { label: 'Added labels', field: 'added_labels' },
      { label: 'Closed issues', field: 'closed_issues' },
    ],
  },
  {
    key: 'engage_points',
    label: 'Engage',
    description: 'Event and content points',
    ...numberProps,
    popover: [
      { label: 'Events', field: 'added_events' },
      { label: 'Content', field: 'added_contents' },
    ],
  },
  {
    key: 'discuss_points',
    label: 'Discuss',
    description: 'Forum and Discord points',
    ...numberProps,
    popover: [
      { label: 'Discord message', field: 'discord_messages' },
      { label: 'Discord replies', field: 'discord_replies' },
      { label: 'Forum post', field: 'forum_posts' },
      { label: 'Forum reply', field: 'forum_replies' },
    ],
  },
  {
    key: 'translate_points',
    label: 'Translate',
    description: 'Translation points',
    ...numberProps,
    popover: [
      { label: 'Translated strings', field: 'translated_strings' },
    ],
  },
  {
    key: 'bonus_points',
    label: 'Other',
    description: 'Bonus points',
    ...numberProps,
    popover: [
      { label: 'AI catalog', field: 'ai_contributions' },
      { label: 'Bonuses', field: 'bonuses' },
    ],
  },
  {
    key: 'score',
    label: 'Score',
    ...numberProps,
  },
];

const removeField = filter => allFields.filter(field => field.key !== filter);

const fields = computed(() => props.hackathon ? removeField('contributor_level') : removeField('prize'));

const items = computed(() => {
  const urlParams = new URLSearchParams(window.location.search);
  const highlightUsername = urlParams.get('highlight_username');

  const contributors = props.contributors.map((item) => {
    const contributor = { ...item };
    if (item.current_user || (item.username === highlightUsername)) {
      contributor._rowVariant = 'row-me';
    }

    return contributor;
  });

  if (!props.totals) {
    return contributors;
  }

  const totalsRow = [{
    username: 'table-total',
    level: null,
    ...props.totals,
  }];

  return totalsRow.concat(contributors);
});

const usernames = computed(() => {
  const allContributors = items.value.filter(item => item.username !== 'table-total');
  const scored = allContributors.filter(({ score }) => score > 0).map(({ username }) => username);
  const unscored = allContributors.filter(({ score }) => score == 0).map(({ username }) => username);

  return [...scored, '<!-- Start zero scored below -->', ...unscored];
});

const copyToClipboard = async (data) => {
  await navigator.clipboard.writeText(data);
};

const setTempFlag = (reference) => {
  reference.value = true;
  setTimeout(() => {
    reference.value = false;
  }, 2000);
};

const copyTableData = async () => {
  const dataToExport = {
    totals: props.totals,
    contributors: props.contributors,
  };
  await copyToClipboard(JSON.stringify(dataToExport, null, 2));
  setTempFlag(justCopiedTable);
};

const copyUsernameData = async () => {
  await copyToClipboard(usernames.value.join('\n'));
  setTempFlag(justCopiedUsernames);
};
</script>

<template>
  <TableWithBusyState
    :fields="fields"
    :items="items"
    :busy="busy"
    :sticky-header="stickyHeader"
    show-empty
    hover
    :class="totals ? 'with-totals' : ''"
  >
    <template
      v-for="field in fields.filter(f => f.description)"
      :key="field.key"
      #[`head(${field.key})`]
    >
      <span
        v-b-tooltip="{ title: field.description, delay: { hide: 0 } }"
        class="with-help-text"
        tabindex="0"
        :aria-label="field.description"
      >
        {{ field.label }}
      </span>
    </template>
    <template #head(username)>
      <div class="d-inline-flex">
        Username
        <BLink
          v-if="hackathon"
          class="ms-2 text-secondary"
          @click="copyUsernameData"
        >
          <PajamasReviewCheckmark v-if="justCopiedUsernames" />
          <PajamasCopyToClipboard v-else />
        </BLink>
      </div>
    </template>

    <template #head(score)="{ field }">
      <div class="d-inline-flex">
        {{ field.label }}
        <PointsDocumentationTooltip />
      </div>
    </template>
    <template #cell(username)="{ value }">
      <div
        v-if="value === 'table-total'"
        class="d-inline-flex"
      >
        Total
        <BLink
          class="ms-2 text-secondary"
          @click="copyTableData"
        >
          <PajamasReviewCheckmark v-if="justCopiedTable" />
          <PajamasCopyToClipboard v-else />
        </BLink>
      </div>

      <UserWithGitLabProfileLink
        v-else
        :username="value"
        :filter-data="filterData"
      />
    </template>
    <template #cell(contributor_level)="{ value, item }">
      <ContributorLevelBadge
        :contributor-level="value"
        :team-member="item.team_member"
        :core-team="item.core_team"
      />
    </template>
    <template #cell(prize)="{ index }">
      <div
        v-if="hackathon && index == 0"
        class="p-2"
      >
        {{ contributors.length }}
      </div>
      <HackathonWinnerBadge
        v-else-if="hackathon && [1, 2, 3].includes(index)"
        :prize="index"
      />
    </template>
    <template
      v-for="field in fields.filter(f => f.popover)"
      :key="field.key"
      #[`cell(${field.key})`]="{ item, unformatted, value }"
    >
      <template v-if="unformatted == 0">0</template>
      <template v-else>
        <BPopover
          :close-on-hide="true"
          :delay="{ show: 0, hide: 0 }"
          :tooltip="true"
        >
          <template #target>
            <span>{{ value }}</span>
          </template>
          <template
            v-for="figure in field.popover"
            :key="figure.field"
          >
            <span>
              {{ figure.label }}: {{ item[figure.field] }}
            </span>
            <br>
          </template>
        </BPopover>
      </template>
    </template>
  </TableWithBusyState>
</template>
